import { useEffect, useState } from 'react';
import type { FormEvent, KeyboardEvent } from 'react';
import { Card } from './Card';
import { Mic } from './Mic';
import './BrainPrompt.css';

type VoicePhase =
  | 'idle' | 'requesting' | 'armed' | 'listening' | 'hearing'
  | 'transcribing' | 'ended' | 'error' | 'unsupported';

interface BrainPromptProps {
  hint?: string;
  placeholder?: string;
  ambient?: boolean;
  listening?: boolean;
  voiceStatus?: string | null;
  /** Live partial transcript from speech recognition — appears in the UI as
   *  words are heard so the coach has immediate evidence the mic is working. */
  interim?: string;
  /** Distilled lifecycle phase for the status badge. */
  voicePhase?: VoicePhase;
  /** Mic permission state from the Permissions API. Used to flag a denied
   *  permission up-front so the coach knows why nothing is happening. */
  micPermission?: 'granted' | 'denied' | 'prompt' | 'unknown';
  /** 'hold' = push-to-talk mic (default). 'toggle' = click-once-to-arm. */
  micMode?: 'hold' | 'toggle';
  onSubmit?: (text: string) => void;
  onMicStart?: () => void;
  onMicStop?: () => void;
}

const PHASE_BADGE: Record<VoicePhase, { label: string; tone: string } | null> = {
  idle: null,
  requesting: { label: 'requesting mic…', tone: 'amber' },
  armed: { label: 'mic armed — speak', tone: 'amber' },
  listening: { label: 'listening', tone: 'green' },
  hearing: { label: 'hearing you', tone: 'green' },
  transcribing: { label: 'transcribing…', tone: 'amber' },
  ended: null,
  error: { label: 'mic error', tone: 'red' },
  unsupported: { label: 'unsupported', tone: 'red' },
};

export function BrainPrompt({
  hint = 'tap and tell me what you need',
  placeholder = "say 'plan tonight' or 'how did it go?'",
  ambient = false,
  listening,
  voiceStatus,
  interim,
  voicePhase,
  micPermission,
  micMode = 'hold',
  onSubmit,
  onMicStart,
  onMicStop,
}: BrainPromptProps) {
  const badge = voicePhase ? PHASE_BADGE[voicePhase] : null;
  const permissionDenied = micPermission === 'denied';
  const [text, setText] = useState('');
  const [showInput, setShowInput] = useState(false);

  // Reveal the textarea when listening starts so the prompt is visibly
  // active — the live transcript block sits above it.
  useEffect(() => {
    if (listening) setShowInput(true);
  }, [listening]);

  const submit = () => {
    const value = text.trim();
    if (!value) return;
    onSubmit?.(value);
    setText('');
    setShowInput(false);
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    submit();
  };
  const handleKey = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
    if (e.key === 'Escape') {
      setShowInput(false);
      setText('');
    }
  };

  return (
    <Card variant="glass" ambient={ambient}>
      <div className="brain-prompt__inner">
        <div className="brain-prompt__row">
          <Mic listening={listening} mode={micMode} onStart={onMicStart} onStop={onMicStop} />
          <div className="brain-prompt__waveform" data-active={listening || undefined} aria-hidden="true">
            <span className="brain-prompt__bar" />
            <span className="brain-prompt__bar" />
            <span className="brain-prompt__bar" />
            <span className="brain-prompt__bar" />
            <span className="brain-prompt__bar" />
          </div>
          {badge && (
            <span className="brain-prompt__badge" data-tone={badge.tone}>
              <span className="brain-prompt__badge-dot" />
              {badge.label}
            </span>
          )}
        </div>
        {permissionDenied && (
          <div className="brain-prompt__perm-warning" role="alert">
            Microphone blocked. Open chrome://settings/content/microphone, allow
            localhost:5180, then refresh the page.
          </div>
        )}
        {voiceStatus && <div className="brain-prompt__voice-status" aria-live="polite">{voiceStatus}</div>}
        {(listening || voicePhase === 'transcribing' || (interim && interim.length > 0)) && (
          <div className="brain-prompt__interim" aria-live="polite" data-empty={!interim || undefined}>
            <span className="brain-prompt__interim-label">
              {voicePhase === 'transcribing' ? 'Transcribing' : listening ? 'Hearing' : 'Heard'}
            </span>
            <span className="brain-prompt__interim-text">
              {interim && interim.length > 0 ? interim : 'speak now…'}
            </span>
            {listening && (
              <span className="brain-prompt__interim-hint">
                Pause for a moment and I&apos;ll send it. Or click the orb to send now.
              </span>
            )}
            {voicePhase === 'transcribing' && (
              <span className="brain-prompt__interim-hint">
                Polishing the transcript through Whisper for accent-robust accuracy…
              </span>
            )}
          </div>
        )}
        {!showInput && (
          <button
            type="button"
            className="brain-prompt__hint"
            onClick={() => setShowInput(true)}
            aria-label={`Open the Brain prompt — ${hint}`}
          >
            {hint}
          </button>
        )}
        {showInput && (
          <form className="brain-prompt__form" onSubmit={handleSubmit} aria-label="Talk to Brain">
            <textarea
              autoFocus
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={handleKey}
              placeholder={placeholder}
              rows={1}
              className="brain-prompt__input"
              aria-label="Brain prompt"
              data-testid="brain-prompt-input"
            />
          </form>
        )}
      </div>
    </Card>
  );
}
